import React, { useState } from 'react';

import PhotoEditorServiceModal from 'components/modals/photo_editor_service_modal';
import PhotoBulkActions from 'components/photos/bulk_actions';
import PhotoCard from 'components/photos/photo_card';
import PhotoRow from 'components/photos/photo_row';
import { reorderableList } from 'helpers/react_dnd/reorderable_list';
import { NEW_PHOTO_ATTACHMENT_ID_PREFIX } from 'helpers/photos/constants';
import { Alert } from 'react-bootstrap-5';

// TODO: clean this type up
type PhotoAttachment = {
  url?: string,
  fullUrl?: string,
  id: number | string,
  syndicate?: boolean,
  processing?: boolean,
  deleted?: boolean,
  thumbnailUrl?: string,
  originalUploadId?: string | number,
  uploadId?: string | number,
  uploaded?: boolean,
  dataContentType?: string,
  sortOrder?: number,
  sidekiqJobId?: string | null,
  sidekiqStatus?: string,
  attachmentType?: string,
  description?: string,
  digitallyAltered?: boolean,
  originalUrl?: string
}

type Props = {
  bulkDownloadPhotosLink: string,
  classSlug: string,
  defaultPhotoOption: string,
  digitallyAlteredEnabled?: boolean,
  maxPhotos: number,
  photoAttachments: Array<PhotoAttachment>,
  photoEditorUrl: string,
  photoTypeOptions: Array<string>,
  showEdit: boolean,
  showLimit: boolean,
  syndication: boolean,
  userAdmin?: boolean,
  onPhotoChange: (photos: Array<PhotoAttachment>) => void
}

function PhotoForm({
  bulkDownloadPhotosLink,
  classSlug,
  defaultPhotoOption,
  digitallyAlteredEnabled,
  maxPhotos,
  photoAttachments,
  photoEditorUrl,
  photoTypeOptions,
  showEdit = true,
  showLimit = false,
  syndication,
  userAdmin,
  onPhotoChange
}: Props) {
  const [photoToEditId, setPhotoToEditId] = useState<number | string>();
  const [selectedPhotoIds, setSelectedPhotoIds] = useState<(number | string)[]>([]);
  const photoToEdit = photoAttachments.find(photo => photo.id === photoToEditId);

  function showSyndicationPhotoWarning() {
    return !photoAttachments.some(pa => pa.syndicate);
  }

  function rowKey(photoAttachment) {
    const { id, uploadId, photoId, copy } = photoAttachment;

    if (id) return id;
    if (copy) return `c${photoId}`;
    if (uploadId) return `u${uploadId}`;
  }

  function allowUpload() {
    if (maxPhotos && photoAttachments.length >= maxPhotos) {
      alert(`Cannot exceed ${maxPhotos} photos`);
      return false;
    } else {
      return true;
    }
  }

  function handlePhotoAttachmentsUpdate(updatedPhotoAttachments: Array<PhotoAttachment>) {
    onPhotoChange(updatedPhotoAttachments);
  }

  // Applies one or more attribute changes to a single photo in a single update. Callers pass an object of
  // changes ({ syndicate: true }, { digitallyAltered: true, originalUrl }, ...). Because photoAttachments is
  // a prop, fields that change together must be set in one call: two back-to-back calls would each read the
  // same stale array and the second would clobber the first.
  function handleAttributeChange(index: number, changes: Partial<PhotoAttachment>) {
    const updatedPhotoAttachments = [...photoAttachments];
    updatedPhotoAttachments[index] = { ...updatedPhotoAttachments[index], ...changes };
    handlePhotoAttachmentsUpdate(updatedPhotoAttachments);
  }

  function handleBulkAttributeChange(attribute: string, value) {
    const updatedPhotoAttachments = photoAttachments.map((photoAttachment) => {
      if (selectedPhotoIds.includes(rowKey(photoAttachment))) {
        return { ...photoAttachment, [attribute]: value };
      }

      return photoAttachment;
    });
    handlePhotoAttachmentsUpdate(updatedPhotoAttachments);
  }

  function handleUpload(data) {
    const newPhotoAttachment: PhotoAttachment = {
      url: data.url,
      thumbnailUrl: data.url,
      originalUploadId: data.id,
      uploadId: data.id, //unnecessary?
      uploaded: true,
      dataContentType: data.file_type,
      id: `${NEW_PHOTO_ATTACHMENT_ID_PREFIX}${data.id}`,
    };

    const updatedPhotoAttachments = [...photoAttachments, newPhotoAttachment];
    handlePhotoAttachmentsUpdate(updatedPhotoAttachments);
  }

  function handlePhotoReorder(dragIndex: number, hoverIndex: number) {
    const photoAttachmentsShallow = [...photoAttachments];
    const dragPhoto = photoAttachmentsShallow[dragIndex];
    // in rare cases, dragPhoto can return undefined.
    // we are unsure on why it happens, but it seems like a very rare edge case.
    if (!dragPhoto) {
      handlePhotoAttachmentsUpdate(photoAttachmentsShallow);
      return;
    }

    photoAttachmentsShallow.splice(dragIndex, 1);
    photoAttachmentsShallow.splice(hoverIndex, 0, dragPhoto);
    const updatedPhotoAttachments = photoAttachmentsShallow.map((photoAttachment, index) => (
      { ...photoAttachment, sortOrder: index }
    ));
    handlePhotoAttachmentsUpdate(updatedPhotoAttachments);
  }

  function handleCloseEditModal() {
    setPhotoToEditId(undefined);
  }

  function handleOpenEditModal(photoForEdit: PhotoAttachment) {
    setPhotoToEditId(photoForEdit.id);
  }

  function handlePhotoEditSave(photoUpdate) {
    const updatedPhotoAttachments = photoAttachments.map((photoAttachment) => {
      if (photoAttachment.id === photoToEditId) {
        return { ...photoAttachment, uploadId: photoUpdate.id, url: photoUpdate.url, thumbnailUrl: photoUpdate.url };
      }

      return photoAttachment;
    });

    handlePhotoAttachmentsUpdate(updatedPhotoAttachments);
    setPhotoToEditId(undefined);
  }

  function renderPhotoEditor() {
    if (!photoEditorUrl) return;

    return (
      <PhotoEditorServiceModal

        imageUrl={photoToEdit ? photoToEdit.url : ''}
        photoEditorUrl={photoEditorUrl}
        show={!!photoToEdit}
        onHide={handleCloseEditModal}
        onSave={handlePhotoEditSave}
      />
    );
  }

  function renderNoSyndicatablePhotosWarning() {
    const showWarning = syndication && showSyndicationPhotoWarning();
    if (!showWarning) return null;

    return <Alert variant="warning">
      <div>
        Your listing photos will not be included on third party listing
        sites or Buildout Buyer Network unless they are approved.
      </div>

      <strong> Please review and check the appropriate boxes below for any approved photos.</strong>
    </Alert>;
  }

  function handleBulkCheckboxClicked(id, isChecked) {
    if (isChecked) {
      setSelectedPhotoIds(previousSelected => previousSelected.filter(selectedId => selectedId !== id));
    } else {
      setSelectedPhotoIds(previousSelected => ([...previousSelected, id]));
    }
  }

  function selectedPhotoAttachments() {
    return photoAttachments.filter(photoAttachment => selectedPhotoIds.includes(rowKey(photoAttachment)));
  }

  function handleBulkPreview() {
    selectedPhotoAttachments().forEach(photoAttachment => window.open(photoAttachment.fullUrl || photoAttachment.url, '_blank'));
    setSelectedPhotoIds([]);
  }

  function handleBulkDownload() {
    setTimeout(() => setSelectedPhotoIds([]), 50);
  }

  function allPhotosSelected() {
    return photoAttachments.length > 0 && photoAttachments.every(photoAttachment => (
      selectedPhotoIds.includes(rowKey(photoAttachment))
    ));
  }

  function handleToggleAll() {
    if (allPhotosSelected()) {
      setSelectedPhotoIds([]);
    } else {
      setSelectedPhotoIds(photoAttachments.map(photoAttachment => rowKey(photoAttachment)));
    }
  }

  function bulkDownloadLinkWithIds() {
    if (!bulkDownloadPhotosLink) return;

    const downloadPhotosLink = new URL(bulkDownloadPhotosLink);

    selectedPhotoIds.forEach((photoAttachmentId) => {
      downloadPhotosLink.searchParams.append('photo_attachment_ids[]', photoAttachmentId.toString());
    });

    return downloadPhotosLink.toString();
  }

  return (
    <React.Fragment>
      <div className="d-flex flex-column gap-4">
        <PhotoBulkActions
          allSelected={allPhotosSelected()}
          bulkDownloadLink={bulkDownloadLinkWithIds()}
          disabled={selectedPhotoIds.length === 0}
          showLimit={showLimit}
          onBulkAttributeChange={handleBulkAttributeChange}
          onBulkDelete={() => handleBulkAttributeChange('deleted', true)}
          onBulkDownload={handleBulkDownload}
          onBulkPreview={handleBulkPreview}
          onSelectAll={handleToggleAll}
        />
        {renderNoSyndicatablePhotosWarning()}
        <div className="photo-grid">
          <PhotoCard
            allowUpload={allowUpload}
            onUpload={handleUpload}
          />

          {photoAttachments.map((photoAttachment, index) => {
            const uniqueId = rowKey(photoAttachment);
            const isChecked = selectedPhotoIds.includes(uniqueId);
            return (
              <PhotoRow
                classSlug={classSlug}
                defaultPhotoOption={defaultPhotoOption}
                digitallyAlteredEnabled={digitallyAlteredEnabled}
                index={index}
                isChecked={isChecked}
                key={uniqueId}
                mediaUiUpdate={true}
                photoAttachment={photoAttachment}
                photoEditorUrl={photoEditorUrl}
                photoTypeOptions={photoTypeOptions}
                showEdit={showEdit}
                userAdmin={userAdmin}
                onAttributeChange={handleAttributeChange}
                onCheckboxClicked={() => handleBulkCheckboxClicked(uniqueId, isChecked)}
                onEdit={handleOpenEditModal}
                onReorder={handlePhotoReorder}
              />
            );
          })
          }
        </div>
        {renderPhotoEditor()}
      </div>
    </React.Fragment>
  );
}

export default reorderableList(PhotoForm);
